import React, { type ReactNode, useCallback, useEffect } from 'react';
import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useService } from '@atlassian/jira-common-services/src/use-service/index.tsx';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ObjectActivityTimelineContainer } from '@atlassian/jira-servicedesk-insight-object-activity-timeline-store/src/controllers/store/index.tsx';
import { ObjectDetailsContainer } from '@atlassian/jira-servicedesk-insight-object-details-store/src/ui/index.tsx';
import { getObjectDetailsUrl } from '@atlassian/jira-servicedesk-insight-object-services/src/common/utils.tsx';
import type { InsightObjectDetailsResponse } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import {
	type CmdbObjectId,
	toCmdbObjectId,
	type WorkspaceId,
} from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import { GenericError } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/errors/index.tsx';
import { InsightObjectViewSkeleton } from '@atlassian/jira-skeletons/src/ui/insight-object/index.tsx';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { useFetchObjectDetails } from '../services/fetch-object-details/index.tsx';

export type Props = {
	workspaceId: WorkspaceId;
	objectId: CmdbObjectId;
	initialObjectDetailsData?: InsightObjectDetailsResponse;
	createAnalyticsEvent: CreateUIAnalyticsEvent;
	children: ReactNode;
	onUpdate: () => void;
};

export const ObjectViewContainer = (props: Props) => {
	const {
		workspaceId,
		objectId,
		initialObjectDetailsData,
		createAnalyticsEvent,
		children,
		onUpdate,
	} = props;

	const {
		data: fetchData,
		error: fetchError,
		loading: fetchLoading,
		fetchObjectDetails: fetchObjectDetailsRequest,
	} = useFetchObjectDetails(workspaceId, createAnalyticsEvent);

	// Remove below code during 'fix_duplicate_object_fetch_call' FF cleanup
	const request = useCallback(async (): Promise<InsightObjectDetailsResponse> => {
		try {
			const response = await performGetRequest(getObjectDetailsUrl(workspaceId, objectId));

			fireTrackAnalytics(createAnalyticsEvent({}), 'fetchObjectDetails succeeded');
			return response;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (error: any) {
			fireErrorAnalytics({
				error,
				meta: {
					id: 'fetchObjectDetails',
					packageName: 'jiraServicedeskInsightObjectView',
				},
			});

			throw error;
		}
	}, [workspaceId, objectId, createAnalyticsEvent]);
	const {
		data,
		loading,
		error,
		fetch: fetchObjectDetails,
	} = useService<InsightObjectDetailsResponse>(request);

	let objectDetails = null;
	if (fg('fix_duplicate_object_fetch_call')) {
		objectDetails = fetchData ?? initialObjectDetailsData;
	} else {
		objectDetails = data ?? initialObjectDetailsData;
	}

	useEffect(() => {
		// auto fetch the initial object details if initialObjectDetailsData is not provided or on object change
		if (fg('fix_duplicate_object_fetch_call')) {
			if (!fetchLoading) {
				if (!objectDetails || objectDetails.id !== objectId) {
					fetchObjectDetailsRequest(objectId);
				}
			}
		} else if (!objectDetails || objectDetails.id !== objectId) {
			fetchObjectDetails();
		}
	}, [fetchLoading, fetchObjectDetails, fetchObjectDetailsRequest, objectDetails, objectId]);

	useEffect(() => {
		if (fg('fix_duplicate_object_fetch_call')) {
			if (fetchError) {
				GlobalPageLoadExperience.failure({ force: true });
			}
		} else if (error) {
			GlobalPageLoadExperience.failure({ force: true });
		}
	}, [error, fetchError]);

	if (fg('fix_duplicate_object_fetch_call')) {
		if (fetchLoading) {
			return <InsightObjectViewSkeleton />;
		}
		if (fetchError) {
			return <GenericError />;
		}
	} else {
		if (loading) {
			return <InsightObjectViewSkeleton />;
		}
		if (error) {
			return <GenericError />;
		}
	}

	return objectDetails != null ? (
		<ObjectDetailsContainer
			scope={objectId}
			objectId={toCmdbObjectId(objectId)}
			workspaceId={workspaceId}
			initialObjectDetailsData={objectDetails}
			createAnalyticsEvent={createAnalyticsEvent}
			onUpdate={onUpdate}
		>
			<ObjectActivityTimelineContainer
				workspaceId={workspaceId}
				createAnalyticsEvent={createAnalyticsEvent}
				objectId={toCmdbObjectId(objectId)}
			>
				{children}
			</ObjectActivityTimelineContainer>
		</ObjectDetailsContainer>
	) : (
		<InsightObjectViewSkeleton />
	);
};
